import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderWebsiteVersionGlobalStyles } from "redux/reducers/models/websiteBuilder";
import { clone } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateGlobalStyles } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { Select } from "components/select/index";
import { WebsiteBuilderNumberInput } from "pages/secure/facility/websiteBuilder/editor/components/numberInput/WebsiteBuilderNumberInput";
import { IWebsiteBuilderToolbarMenuViewSection, WebsiteBuilderToolbarMenuView } from "../../../WebsiteBuilderToolbarMenuView";

interface IFontsViewProps {
  goBackToParentView: () => void;
}

const DEFAULT_FONT_PLACEHOLDER = -1;

const BODY_TEXT_MIN_FONT_SIZE = 12;
const BODY_TEXT_MAX_FONT_SIZE = 48;

export function FontsView(props: IFontsViewProps) {
  const { goBackToParentView } = props;

  const { Option } = Select;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const globalStyles = useAppSelector(store => store.websiteBuilderStore.editor.globalStyles);
  const fonts = useAppSelector(store => store.websiteBuilderStore.editor.fonts);
  
  const [updatedGlobalStyles, setUpdatedGlobalStyles] = useState<IWebsiteBuilderWebsiteVersionGlobalStyles>(globalStyles != null ? clone(globalStyles) : null);

  function dispatchGlobalStyles(
    prevGlobalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles,
    nextGlobalStyles: IWebsiteBuilderWebsiteVersionGlobalStyles,
  ) {
    dispatch(
      websiteBuilderUpdateGlobalStyles({
        globalStyles: clone(nextGlobalStyles),
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: globalStyles,
    updatedState: updatedGlobalStyles,
    setUpdatedState: setUpdatedGlobalStyles,
    dispatchState: dispatchGlobalStyles,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.SHORT,
  });

  function handleChangeBodyTextFontSize(body_text_font_size: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      body_text_font_size,
    }));
  }

  function renderFontOptions() {
    return (
      <>
        <Option value={DEFAULT_FONT_PLACEHOLDER}>&nbsp;</Option>
        {fonts?.map(font => (
          <Option key={font.id} value={font.id}>
            <div style={{ fontFamily: `"${font.font_family}", ${font.generic_font_family}` }}>{font.label}</div>
          </Option>
        ))}
      </>
    );
  }

  function renderHeadingFontSection() {
    return (
      <Select
        label={t("Font")}
        onChange={fontId =>
          handleUpdateState(prev => ({
            ...prev,
            heading_font_id: fontId === DEFAULT_FONT_PLACEHOLDER ? null : fontId,
          }))
        }
        defaultValue={updatedGlobalStyles.heading_font_id ?? DEFAULT_FONT_PLACEHOLDER}
      >
        {renderFontOptions()}
      </Select>
    );
  }

  function renderBodyTextFontSection() {
    return (
      <>
        <Select
          label={t("Font")}
          onChange={fontId =>
            handleUpdateState(prev => ({
              ...prev,
              body_text_font_id: fontId === DEFAULT_FONT_PLACEHOLDER ? null : fontId,
            }))
          }
          defaultValue={updatedGlobalStyles.body_text_font_id ?? DEFAULT_FONT_PLACEHOLDER}
        >
          {renderFontOptions()}
        </Select>
        <WebsiteBuilderNumberInput
          label={t("Size (px)")}
          initialValue={updatedGlobalStyles.body_text_font_size}
          min={BODY_TEXT_MIN_FONT_SIZE}
          max={BODY_TEXT_MAX_FONT_SIZE}
          required={false}
          onChange={handleChangeBodyTextFontSize}
        />
      </>
    );
  }

  const sections: IWebsiteBuilderToolbarMenuViewSection[] = updatedGlobalStyles ? [
    {
      title: t("Headings"),
      content: renderHeadingFontSection(),
    },
    {
      title: t("Body Text"),
      content: renderBodyTextFontSection(),
    },
  ] : [];

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Fonts")}
      goBackToParentView={goBackToParentView}
      sections={sections}
    />
  );
}
